import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { isModifiedClick } from "@/lib/link-utils";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Trash2, Edit2, Check, X, Loader2, Rss, Pin, PinOff } from "lucide-react";
import { Conversation } from "@/api/models/api-types";
import { useChangeConversationName } from "@/api/hooks/use-change-conversation-name";
import { useToast } from "@/features/toast";
import { useTranslation } from "react-i18next";
import { useStreamingStore } from "@/features/chat/store/streaming-store";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { useConversationModule } from "../hooks/use-conversation-module";

/** Sidebar daraltıldığında sohbet adı yerine gösterilecek kısa baş harfler (avatar gibi) */
const getInitials = (name: string) => {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

interface ConversationItemProps {
  conversation: Conversation;
  isActive: boolean;
  isLoading?: boolean; // 🔄 Loading state
  isSidebarOpen: boolean;
  isPinned?: boolean;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  onTogglePin?: (id: string) => void;
}

export const ConversationItem: React.FC<ConversationItemProps> = ({
  conversation,
  isActive,
  isLoading = false, // 🔄 Loading state
  isSidebarOpen,
  isPinned = false,
  onSelect,
  onDelete,
  onTogglePin,
}) => {
  const { t } = useTranslation();
  const { success, error } = useToast();
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(conversation.name);
  const shouldReduceMotion = useReducedMotion();
  const [isHovered, setIsHovered] = useState(false);
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia("(min-width: 768px)").matches;
  });

  const { mutate: changeConversationName, isPending } =
    useChangeConversationName();

  // ===== Streaming / Unread Indicators (ChatGPT-style) =====
  const session = useStreamingStore(
    (state) => state.sessionsByChat[conversation._id]
  );
  const markChatSeen = useStreamingStore((state) => state.markChatSeen);

  const { isStreaming, hasUnread, isPublic } = useMemo(() => {
    return {
      isStreaming: session?.status === "streaming",
      hasUnread: !!session?.hasUnread,
      // Public sohbetler için sidebar'da küçük bir indicator göstereceğiz
      isPublic: !!conversation.shareToken,
    };
  }, [session?.status, session?.hasUnread, conversation.shareToken]);

  // Conversation ismini sidebar için kısalt
  const getTruncatedName = (name: string, maxLength: number = 30) => {
    if (!name || name.trim() === "") return t("chat.untitled");

    const trimmedName = name.trim();
    if (trimmedName.length <= maxLength) {
      return trimmedName;
    }

    // Uzun isimleri akıllıca kısalt
    // Önce kelime sınırlarında kesmeye çalış
    const words = trimmedName.split(/\s+/);
    let truncated = "";

    for (const word of words) {
      if ((truncated + " " + word).length <= maxLength - 3) {
        truncated += (truncated ? " " : "") + word;
      } else {
        break;
      }
    }

    // Eğer hiç kelime sığmazsa, ilk maxLength-3 karakteri al
    if (!truncated) {
      truncated = trimmedName.substring(0, maxLength - 3);
    }

    return truncated + "...";
  };

  const handleSaveName = () => {
    if (!editName.trim()) {
      error(t("chat.nameCannotBeEmpty"), {
        title: t("error.title"),
      });
      return;
    }

    if (editName.trim() === conversation.name) {
      setIsEditing(false);
      return;
    }

    changeConversationName(
      { conversationId: conversation._id, newName: editName.trim() },
      {
        onSuccess: () => {
          setIsEditing(false);
          success(t("chat.nameUpdateSuccess"), {
            title: t("common.success"),
            // Note: No undo action - name change is immediate and doesn't need undo
          });
        },
        onError: (apiError: any) => {
          error(apiError?.message || t("chat.nameUpdateError"), {
            title: t("error.title"),
            actions: [
              {
                label: t("common.retry"),
                action: () => handleSaveName(),
              },
            ],
          });
        },
      }
    );
  };

  const handleCancelEdit = () => {
    setEditName(conversation.name);
    setIsEditing(false);
  };

  /**
   * Satır artık `<a href="/chat/:id">`; Ctrl/Cmd/orta tık tarayıcıya bırakılır
   * (sohbet yeni sekmede açılır). Düz sol tıkta ise preventDefault ile mevcut
   * imperatif akış korunur — mobilde sidebar kapatma gibi yan etkiler orada.
   */
  const handleSelectConversation = (e?: React.MouseEvent) => {
    if (e) {
      if (isModifiedClick(e)) return;
      e.preventDefault();
    }
    // Eğer bu chat için unread dot varsa, seçildiğinde temizle
    if (hasUnread) {
      markChatSeen(conversation._id);
    }
    onSelect(conversation._id);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mediaQuery = window.matchMedia("(min-width: 768px)");
    const onChange = () => setIsDesktop(mediaQuery.matches);
    onChange();
    mediaQuery.addEventListener?.("change", onChange);
    return () => mediaQuery.removeEventListener?.("change", onChange);
  }, []);

  const actionsExpanded = !isDesktop || isHovered;
  // Sohbetin modülü: öğe o modülün rengine boyanır, hub'da küçük ikon görünür
  const { module: chatModule, showBadge, scopeStyle } = useConversationModule(conversation);
  const ModuleIcon = chatModule?.icon;
  const collapsedWidth = 36; // share/indicator alanı
  const expandedWidth = 116; // share + pin + edit + delete

  return (
    <motion.div
      style={scopeStyle}
      className={cn(
        "group relative flex items-center w-full rounded-lg transition-colors",
        chatModule && "module-scope",
        isSidebarOpen
          ? "hover:bg-bg-secondary dark:hover:bg-gray-800"
          : "justify-center",
        isActive && "bg-module-accent-soft dark:bg-module-accent/10"
      )}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
    >
      {/* Main Conversation Button/Input */}
      {isEditing && isSidebarOpen ? (
        <div className="flex items-center space-x-1 p-2">
          <Input
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleSaveName();
              if (e.key === "Escape") handleCancelEdit();
            }}
            className="flex-1 h-7 text-sm"
            autoFocus
            disabled={isPending}
            placeholder={t("chat.untitled")}
            maxLength={100} // Edit sırasında maksimum uzunluk sınırı
          />
          <Button
            variant="ghost"
            size="sm"
            onClick={handleSaveName}
            disabled={isPending}
            className="h-7 w-7 p-0 text-green-600 hover:text-green-700"
            title={t("chat.saveName")}
          >
            <Check className="h-4 w-4" />
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleCancelEdit}
            disabled={isPending}
            className="h-7 w-7 p-0 text-red-600 hover:text-red-700"
            title={t("chat.cancelEdit")}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <Button
          asChild
          variant={"ghost"}
          size="sm"
          className={cn(
            "transition-all duration-300 relative overflow-hidden",
            isSidebarOpen
              ? "w-full justify-start text-left"
              : "w-8 h-8 p-0 justify-center items-center",
            // Sidebar kapalıyken hover efekti yok (minimal tutmak için)
            isSidebarOpen &&
              !isActive &&
              "hover:bg-accent/50 hover:border-accent/30",
            // 🔄 Loading effects
            isLoading && "conversation-loading-border cursor-wait",
            isLoading && !isActive && "conversation-loading",
            // `<a>` native `disabled` desteklemez; tıklamayı CSS ile engelliyoruz.
            isLoading && "pointer-events-none"
          )}
          disabled={isLoading} // aria-disabled için; tıklama engeli pointer-events ile
        >
          <Link
            to={`/chat/${conversation._id}`}
            onClick={handleSelectConversation}
          >
          {isSidebarOpen ? (
            <div className="flex items-center gap-2 w-full min-w-0">
              {/* 🔄 Loading icon (conversation-level) */}
              {isLoading && (
                <Loader2 className="h-4 w-4 flex-shrink-0 animate-spin text-module-accent" />
              )}

              {/* Modül işareti — yalnızca hub'da (tüm liste görünürken) */}
              {showBadge && ModuleIcon && !isLoading && (
                <span
                  className="flex size-4 flex-shrink-0 items-center justify-center text-module-accent"
                  title={chatModule ? t(chatModule.nameKey) : undefined}
                  aria-label={chatModule ? t(chatModule.nameKey) : undefined}
                >
                  <ModuleIcon size={14} className="size-3.5" />
                </span>
              )}

              <span
                className={cn(
                  "transition-opacity duration-300 block truncate text-md font-medium flex-1 min-w-0",
                  isLoading && "opacity-70",
                  isActive && "text-module-accent dark:text-module-accent"
                )}
                title={conversation.name || t("chat.untitled")} // Full name tooltip
              >
                {getTruncatedName(conversation.name || t("chat.untitled"))}
              </span>
            </div>
          ) : (
            // ✅ Sidebar kapalıyken - Tamamen minimal, sadece baş harfler (border yok, arka plan yok)
            !isLoading && (
              <div className="relative w-8 h-8">
                <div
                  className={cn(
                    "flex items-center justify-center w-8 h-8 rounded-lg text-xs font-semibold transition-all",
                    isActive
                      ? "bg-muted dark:bg-primary/10 text-module-accent dark:text-module-accent"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                  title={conversation.name || t("chat.untitled")}
                >
                  {getInitials(conversation.name || t("chat.untitled"))}
                </div>

                {/* Indicators (collapsed sidebar) */}
                {isPinned && !isStreaming && (
                  <Pin className="absolute -bottom-0.5 -left-0.5 h-2.5 w-2.5 fill-current text-gray-400 dark:text-gray-500" />
                )}
                {isStreaming && (
                  <Loader2 className="absolute -top-0.5 -right-0.5 h-3 w-3 animate-spin text-module-accent" />
                )}
                {!isStreaming && hasUnread && !isActive && (
                  <span className="absolute top-0 right-0 h-2 w-2 rounded-full bg-module-accent" />
                )}
              </div>
            )
          )}
          </Link>
        </Button>
      )}

      {/* Right Overlay (Indicators + Actions) */}
      {isSidebarOpen && !isEditing && !isLoading && (
        <motion.div
          className={cn(
            "absolute right-2 top-1/2 -translate-y-1/2 z-10",
            "rounded-lg",
            "hover:bg-bg-primary dark:hover:bg-gray-900"
          )}
          initial={false}
          animate={
            shouldReduceMotion
              ? { width: expandedWidth }
              : { width: actionsExpanded ? expandedWidth : collapsedWidth }
          }
          transition={
            shouldReduceMotion
              ? { duration: 0 }
              : { type: "spring", stiffness: 420, damping: 34 }
          }
          onClick={(e) => {
            // Overlay'e tıklayınca da sohbet seçilsin (boşluk click-block yapmasın)
            e.stopPropagation();
            handleSelectConversation();
          }}
        >
          <div className="relative flex items-center justify-end gap-1 h-7 px-1">
            {/* Chat status indicators (pin/share/stream/unread) - always visible */}
            <div className="flex items-center gap-2 pr-1">
              {isPinned && !isStreaming && (
                <Pin
                  className={cn(
                    "h-3.5 w-3.5 fill-current",
                    isActive
                      ? "text-module-accent dark:text-module-accent"
                      : "text-gray-400 dark:text-gray-500"
                  )}
                  aria-label={t("chat.pinned")}
                />
              )}
              {isPublic && !isStreaming && (
                <Rss
                  className={cn(
                    "h-3.5 w-3.5",
                    isActive
                      ? "text-module-accent dark:text-module-accent"
                      : "text-gray-500 dark:text-gray-400"
                  )}
                  aria-label="Public chat"
                />
              )}
              {isStreaming && (
                <Loader2 className="h-4 w-4 animate-spin text-module-accent" />
              )}
              {!isStreaming && hasUnread && !isActive && (
                <span
                  className="h-2.5 w-2.5 rounded-full bg-module-accent"
                  aria-label={t("common.unread")}
                />
              )}
            </div>

            {/* Actions - reveal on hover (desktop) / always visible (mobile) */}
            <AnimatePresence initial={false}>
              {actionsExpanded && (
                <motion.div
                  key="actions"
                  className="flex items-center gap-1"
                  initial={shouldReduceMotion ? false : { opacity: 0, x: 6 }}
                  animate={
                    shouldReduceMotion ? { opacity: 1 } : { opacity: 1, x: 0 }
                  }
                  exit={
                    shouldReduceMotion ? { opacity: 0 } : { opacity: 0, x: 6 }
                  }
                  transition={
                    shouldReduceMotion
                      ? { duration: 0 }
                      : { duration: 0.15, ease: "easeOut" }
                  }
                >
                  {/* Pin/Unpin Button */}
                  {onTogglePin && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTogglePin(conversation._id);
                      }}
                      className={cn(
                        "hover:text-module-accent hover:bg-module-accent/10 transition-colors h-6 w-6 p-0 rounded-lg"
                      )}
                      title={
                        isPinned ? t("chat.unpinChat") : t("chat.pinChat")
                      }
                    >
                      {isPinned ? (
                        <PinOff className="h-3 w-3" />
                      ) : (
                        <Pin className="h-3 w-3" />
                      )}
                    </Button>
                  )}

                  {/* Edit Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsEditing(true);
                    }}
                    className={cn(
                      "hover:text-cyan-400 hover:bg-cyan-500/10 transition-colors h-6 w-6 p-0 rounded-lg"
                    )}
                    title={t("chat.editName")}
                  >
                    <Edit2 className="h-3 w-3" />
                  </Button>

                  {/* Delete Button */}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      onDelete(conversation._id);
                    }}
                    className={cn(
                      "hover:text-red-400 hover:bg-red-500/10 transition-colors h-6 w-6 p-0 rounded-lg"
                    )}
                    title={t("chat.deleteChat")}
                  >
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      )}
    </motion.div>
  );
};
